/**
 * EchoProvider — Manages Laravel Echo / Reverb connection lifecycle.
 *
 * Replaces SocketContext (Socket.io) with Laravel Echo over the Pusher
 * protocol.  Reverb speaks the same wire format, so `pusher-js` is the
 * transport and `laravel-echo` wraps it.
 *
 * Install once:
 *   npm install laravel-echo pusher-js
 *   (then remove socket.io-client from package.json)
 *
 * .env.development (Vite):
 *   VITE_REVERB_APP_KEY=your_reverb_app_key
 *   VITE_REVERB_HOST=localhost
 *   VITE_REVERB_PORT=8080
 *   VITE_REVERB_SCHEME=http
 *
 * Usage (identical shape to the old useSocket):
 *   const { echo, connected } = useEcho();
 *   echo.private(`App.Models.User.${userId}`).listen('.NotificationNew', cb);
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { useAuth } from '@hooks/useAuth';

// ─── Vite env vars ─────────────────────────────────────────────────────────
const REVERB_APP_KEY = import.meta.env.VITE_REVERB_APP_KEY  || '';
const REVERB_HOST    = import.meta.env.VITE_REVERB_HOST     || 'localhost';
const REVERB_PORT    = Number(import.meta.env.VITE_REVERB_PORT) || 8080;
const REVERB_SCHEME  = import.meta.env.VITE_REVERB_SCHEME   || 'http';
const API_URL        = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/+$/, '');

// Only connect if Reverb is explicitly enabled AND a real key is configured.
// Skips init otherwise (missing key, placeholder like "your_..._here", or
// VITE_REVERB_ENABLED=false) so the browser console doesn't fill with
// WebSocket connection errors when the Reverb daemon isn't running.
const REVERB_ENABLED_FLAG = String(import.meta.env.VITE_REVERB_ENABLED ?? 'true').toLowerCase() !== 'false';
const REVERB_KEY_VALID    = REVERB_APP_KEY.length > 0
  && !REVERB_APP_KEY.startsWith('your_')
  && !REVERB_APP_KEY.endsWith('_here');
const REVERB_ENABLED      = REVERB_ENABLED_FLAG && REVERB_KEY_VALID;

// ─── Context ───────────────────────────────────────────────────────────────
const EchoContext = createContext({ echo: null, connected: false });
export const useEcho = () => useContext(EchoContext);

// ─── Provider ──────────────────────────────────────────────────────────────
export const EchoProvider = ({ children }) => {
  const { user } = useAuth();
  const echoRef    = useRef(null);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (echoRef.current) return;
    if (!REVERB_ENABLED) return;

    const token = localStorage.getItem('accessToken');
    if (!token || !user) return;

    // Make Pusher available globally so Laravel Echo can find it
    window.Pusher = Pusher;

    const echo = new Echo({
      broadcaster: 'reverb',
      key: REVERB_APP_KEY,
      wsHost: REVERB_HOST,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: REVERB_SCHEME === 'https',
      enabledTransports: ['ws', 'wss'],

      // Laravel Sanctum token — sent with every channel auth request
      authEndpoint: `${API_URL}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    });

    // Reverb/Pusher connection events
    echo.connector.pusher.connection.bind('connected', () => setConnected(true));
    echo.connector.pusher.connection.bind('disconnected', () => setConnected(false));
    echo.connector.pusher.connection.bind('failed', () => setConnected(false));
    echo.connector.pusher.connection.bind('unavailable', () => setConnected(false));

    echoRef.current = echo;
  }, [user]);

  const disconnect = useCallback(() => {
    if (echoRef.current) {
      echoRef.current.disconnect();
      echoRef.current = null;
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [user, connect, disconnect]);

  return (
    <EchoContext.Provider value={{ echo: echoRef.current, connected }}>
      {children}
    </EchoContext.Provider>
  );
};

export default EchoProvider;
